/**
 * #1292 — the composer's attachment controls, ONE implementation for both chat
 * composers (ChatPanel and the landing HeroChat). Before this module each kept
 * its own copy of the budget ref, the upload cancel and the send guard, and this
 * branch's review rounds fixed the same bug in each copy separately.
 *
 *   - the per-message 10 MB inline budget (`inlineBudget.ts`), reserved at attach
 *     time against the composer's committed list;
 *   - removing a chip cancels its upload;
 *   - `unsendableAttachmentNotice` — the one decision every send path asks before
 *     anything is sent, truncated or restored.
 */
import { useCallback, useEffect, useRef } from 'react'
import i18n from '@papan/i18n'
import type { Attachment } from './hooks.js'
import { createInlineBudget, inlineBytesOf, MAX_INLINE_MESSAGE_BYTES } from './inlineBudget.js'
import { discardSessionAttachment } from './sessionAttachmentsApi.js'

/**
 * #1292 (owner): never send a message without an attachment the user added, and
 * never send one the server will refuse:
 *   - a failed chip, or one still uploading (it would go out with no bytes);
 *   - files attached directly that add up to more than the message's 10 MB. The
 *     budget prevents this at attach time, but a list can still get there — a
 *     failed send restores its chips next to ones added meanwhile, or an edit
 *     brings back an old message's bytes — and the server would answer 413.
 * `null` = send. Asked by the composer send (after the steer branch, which sends
 * text only) and the three edit-resend paths (before any truncation or restore).
 */
export function unsendableAttachmentNotice(
  list: ReadonlyArray<Pick<Attachment, 'id' | 'status' | 'filename' | 'url' | 'previewUrl' | 'inlineBytes'>>,
): { level: 'error' | 'info'; message: string } | null {
  const failed = list.find(a => a.status === 'error')
  if (failed) return { level: 'error', message: i18n.t('agent:attachmentNotices.failed', { name: failed.filename }) }
  const uploading = list.find(a => a.status === 'uploading')
  if (uploading) return { level: 'info', message: i18n.t('agent:attachmentNotices.uploading', { name: uploading.filename }) }
  // Raw bytes against the 10 MB the attach-time budget enforces — not the
  // server's 16 MB, which is that budget after base64 plus the text. No message
  // can legitimately carry more: before #1292 a send was capped at 1 MB.
  const inline = list.reduce((sum, a) => sum + inlineBytesOf(a), 0)
  if (inline > MAX_INLINE_MESSAGE_BYTES) {
    return { level: 'error', message: i18n.t('agent:attachmentNotices.overBudget', { max: MAX_INLINE_MESSAGE_BYTES / (1024 * 1024) }) }
  }
  return null
}

/** The toast for an edit whose files were restored but whose resend was refused
 *  by `unsendableAttachmentNotice`. `restored` is the restore's own summary. */
export function editNotSentMessage(restored: string, reason: string): string {
  return i18n.t('agent:attachmentNotices.editNotSent', { restored, reason })
}

interface Toaster {
  error: (message: string) => void
  info: (message: string) => void
}

/**
 * The controls, for a composer whose committed attachments are `lists` (by
 * composer key — the session id) and whose live key is `activeKey()`. Both
 * composers keep one list per chat (ChatPanel's and HeroChat's
 * `attachmentsMap`).
 *
 * WHAT HAPPENS TO AN UPLOAD WHOSE CHIP GOES AWAY (#1292 D7 / I3):
 *   - removed by the user (`cancel`), or dropped by an edit opening or being
 *     cancelled (`discard`): a running upload is cancelled, and one that already
 *     landed is deleted on the server at once (`discardSessionAttachment` — the
 *     server deletes only a still-STAGED row, so a chip from an earlier message
 *     is never touched). An upload that lands AFTER its chip was removed is
 *     deleted as soon as it reports in;
 *   - removed while a SEND holds it (`beginSend`, from before the send's first
 *     await until it ends — the X is still clickable until the composer clears):
 *     it isn't sent (`keptForSend`, asked when the send starts), its delete
 *     waits for the send to end (in case the row was promoted anyway — then it
 *     is `attached` and stays), and a failed send doesn't put it back;
 *   - gone from every list any other way: a running upload is cancelled, and
 *     nothing is deleted — a SEND is such a way (the message now references the
 *     row; deleting it would race the send), and so is anything this module
 *     doesn't know about. The staged-row sweep backstops what it misses.
 */
export function useComposerAttachmentControls(opts: {
  lists: Record<string, Attachment[]>
  activeKey: () => string
  workspaceId: string
  toast: Toaster
}) {
  const { lists, toast } = opts
  const activeKeyRef = useRef(opts.activeKey)
  activeKeyRef.current = opts.activeKey
  const workspaceIdRef = useRef(opts.workspaceId)
  workspaceIdRef.current = opts.workspaceId
  // The lists as of the last render; the budget's own pending entries cover the
  // chips added since (see inlineBudget.ts).
  const listsRef = useRef(lists)
  listsRef.current = lists
  const budgetRef = useRef<ReturnType<typeof createInlineBudget> | null>(null)
  if (!budgetRef.current) budgetRef.current = createInlineBudget()
  const budget = budgetRef.current
  /** Running uploads, by chip id, with the session each uploads into. */
  const controllersRef = useRef(new Map<string, { ctrl: AbortController; sessionId: string }>())
  /** Uploads that landed and are not yet known to be sent: chip id → handle. */
  const uploadedRef = useRef(new Map<string, { sessionId: string; handle: string }>())
  /** Chips the user removed while their upload was still running → its session. */
  const cancelledRef = useRef(new Map<string, string>())
  /** Chips a send in flight is taking (id → how many sends hold it). */
  const sendingRef = useRef(new Map<string, number>())
  /** Removed while a send held them: their delete waits for the send to end. */
  const deferredRef = useRef(new Map<string, { sessionId: string; handle: string }>())
  /** Removed by the user while a send held them — a failed send leaves them out. */
  const removedRef = useRef(new Set<string>())
  /** Chip ids seen in a committed list. Only an id seen there and then gone is
   *  treated as gone — an upload registers right after its chip is ADDED, and an
   *  effect from a render before that add must not cancel it. */
  const seenRef = useRef(new Set<string>())
  /** The composer is unmounted: a pick still running must stop, not upload on. */
  const deadRef = useRef(false)
  /** A chat whose tab was closed moves to a new epoch: a pick started before
   *  that is stale (its later files must not upload into the closed chat). */
  const epochsRef = useRef(new Map<string, number>())

  const discardUploaded = useCallback((sessionId: string, handle: string) => {
    void discardSessionAttachment(sessionId, workspaceIdRef.current, handle).catch(() => {
      // Best effort: the staged-row sweep removes what this missed.
    })
  }, [])

  useEffect(() => {
    const live = new Set(Object.values(lists).flat().map(a => a.id))
    budget.settle(live)
    for (const id of seenRef.current) {
      if (live.has(id)) continue
      seenRef.current.delete(id)
      // Gone without `cancel`/`discard` (a send, or a list replaced): stop a
      // running upload; never delete a row that may be the message's now.
      const running = controllersRef.current.get(id)
      if (running) {
        running.ctrl.abort()
        controllersRef.current.delete(id)
      }
    }
    for (const id of live) seenRef.current.add(id)
    // Forget a landed upload only once it is in no list AND no send holds it: a
    // send clears its chips, and a FAILED send puts them back — still unsent and
    // still the user's to remove (review round 5: forgotten at the clear, a
    // restored chip's removal deleted nothing). A sent one is forgotten here after
    // its send ends (or deleted harmlessly on unmount: the server keeps an
    // attached row).
    for (const id of [...uploadedRef.current.keys()]) {
      if (!live.has(id) && !sendingRef.current.has(id)) uploadedRef.current.delete(id)
    }
  }, [lists, budget])

  // Unmounted (a project switch remounts the landing composer; mobile navigation
  // unmounts the panel): its chips are gone with it, unsent — cancel running
  // uploads (one that lands anyway is deleted as it reports in), delete what
  // landed, and stop the rest of any pick still running (`deadRef`: its later
  // files get an aborted signal) — except chips a send still holds. Safe under
  // StrictMode's simulated unmount: on mount nothing is registered yet, and the
  // remount clears `deadRef`. DEV CAVEAT: React Fast Refresh re-runs this cleanup
  // on an edit of the composer while it stays on screen, deleting its landed
  // uploads — re-attach after a hot reload; production never does this.
  useEffect(() => {
    deadRef.current = false
    const controllers = controllersRef.current
    const uploaded = uploadedRef.current
    const sending = sendingRef.current
    const cancelled = cancelledRef.current
    return () => {
      deadRef.current = true
      for (const [id, running] of controllers) {
        if (sending.has(id)) continue
        running.ctrl.abort()
        cancelled.set(id, running.sessionId)
      }
      controllers.clear()
      for (const [id, u] of uploaded) {
        if (!sending.has(id)) discardUploaded(u.sessionId, u.handle)
      }
      uploaded.clear()
    }
  }, [discardUploaded])

  /** Reserve a file's bytes against the composer the chip is about to be added
   *  under — `key` when given (the chat a pick uploads into: files of one pick
   *  are added one by one, and the user may switch chats in between), else the
   *  ACTIVE one. Synchronous; `false` routes the file to upload. */
  const reserveInline = useCallback((id: string, bytes: number, key: string = activeKeyRef.current()): boolean => {
    return budget.reserve(key, id, bytes, listsRef.current[key] || [])
  }, [budget])

  /** A cancel signal for an upload into `sessionId`, aborted if its chip goes. */
  const uploadSignal = useCallback((id: string, sessionId: string): AbortSignal => {
    if (deadRef.current) return AbortSignal.abort()
    const ctrl = new AbortController()
    controllersRef.current.set(id, { ctrl, sessionId })
    return ctrl.signal
  }, [])

  /** One pick of files into `sessionId`. `processFiles` adds and uploads them one
   *  by one, so the pick can outlive its composer or its chat's tab: `alive()`
   *  says whether its next file still belongs anywhere (skip adding it if not),
   *  and `signal(id)` is the upload's cancel signal — already aborted for a stale
   *  pick, so the file never uploads. */
  const pickFor = useCallback((sessionId: string) => {
    const epoch = epochsRef.current.get(sessionId) ?? 0
    const alive = () => !deadRef.current && (epochsRef.current.get(sessionId) ?? 0) === epoch
    return {
      alive,
      signal: (id: string): AbortSignal => (alive() ? uploadSignal(id, sessionId) : AbortSignal.abort()),
    }
  }, [uploadSignal])

  /** An attachment finished (ready, with the upload's `handle` when it was
   *  uploaded) or failed: its upload is no longer cancellable, and a failed one
   *  holds none of the budget. */
  const settled = useCallback((id: string, failed: boolean, handle?: string) => {
    const running = controllersRef.current.get(id)
    controllersRef.current.delete(id)
    if (failed) budget.release(id)
    const cancelledFrom = cancelledRef.current.get(id)
    if (cancelledFrom !== undefined) {
      cancelledRef.current.delete(id)
      // Removed while uploading, and the upload landed anyway: undo it now.
      if (!failed && handle) discardUploaded(cancelledFrom, handle)
      return
    }
    if (!failed && handle && running) uploadedRef.current.set(id, { sessionId: running.sessionId, handle })
  }, [budget, discardUploaded])

  /** The chip is being removed by the user: cancel its upload, delete what
   *  already landed, and free its reservation. */
  const cancel = useCallback((id: string) => {
    const running = controllersRef.current.get(id)
    if (running) {
      running.ctrl.abort()
      controllersRef.current.delete(id)
      cancelledRef.current.set(id, running.sessionId)
    }
    const uploaded = uploadedRef.current.get(id)
    if (uploaded) {
      uploadedRef.current.delete(id)
      // A send that took this chip may be promoting its row right now: deleting
      // it here would fail that send (an unknown handle) — wait for the send.
      if (sendingRef.current.has(id)) deferredRef.current.set(id, uploaded)
      else discardUploaded(uploaded.sessionId, uploaded.handle)
    }
    if (sendingRef.current.has(id)) removedRef.current.add(id)
    budget.release(id)
  }, [budget, discardUploaded])

  /** A send is taking these chips, from before its first await until it ends
   *  (`end()`, in its `finally`). A chip removed in between — its X is still
   *  clickable until the send clears the composer — has its server delete held
   *  until then: a sent row is `attached` by then and the delete leaves it; an
   *  unsent one goes. `wasRemoved(id)` keeps a failed send from restoring it. */
  const beginSend = useCallback((ids: readonly string[]): (() => void) => {
    for (const id of ids) sendingRef.current.set(id, (sendingRef.current.get(id) ?? 0) + 1)
    let ended = false
    return () => {
      if (ended) return
      ended = true
      for (const id of ids) {
        const n = (sendingRef.current.get(id) ?? 1) - 1
        if (n > 0) { sendingRef.current.set(id, n); continue }
        sendingRef.current.delete(id)
        removedRef.current.delete(id)
        const deferred = deferredRef.current.get(id)
        if (deferred) {
          deferredRef.current.delete(id)
          discardUploaded(deferred.sessionId, deferred.handle)
        }
      }
    }
  }, [discardUploaded])

  /** Was this chip removed by the user during the send that holds it? */
  const wasRemoved = useCallback((id: string): boolean => removedRef.current.has(id), [])

  /** What a held send actually sends: its chips minus any the user removed while
   *  it got ready (removing is "don't send this"); `undefined` when none are left.
   *  Ask it when the send STARTS — after its last await, before `end()` — and
   *  never inside a state updater, which React may run after `end()` cleared it. */
  const keptForSend = useCallback((list: readonly Attachment[] | undefined): Attachment[] | undefined => {
    const kept = list?.filter(a => !removedRef.current.has(a.id))
    return kept && kept.length > 0 ? kept : undefined
  }, [])

  /** These chips are being dropped from the composer unsent (an edit opening
   *  over them, or an edit cancelled): exactly as if each were removed. */
  const discard = useCallback((ids: readonly string[]) => {
    // A chip a send holds is being SENT, not dropped (review round 5: an edit
    // opened while the send was getting ready marked it removed, and the message
    // went out without it) — the send takes it from here.
    for (const id of ids) if (!sendingRef.current.has(id)) cancel(id)
  }, [cancel])

  /** A chat's tab is closing: its chips are dropped unsent (`discard`), and a
   *  pick still adding files to it is made stale (`pickFor`), so its later files
   *  neither appear in nor upload into the closed chat. A pick started after the
   *  chat is reopened belongs to it again. */
  const closeKey = useCallback((key: string) => {
    epochsRef.current.set(key, (epochsRef.current.get(key) ?? 0) + 1)
    discard((listsRef.current[key] ?? []).map(a => a.id))
  }, [discard])

  /** A draft became a session: move its chips' key synchronously, before the
   *  re-render, so a paste right after sees them. */
  const carry = useCallback((from: string, to: string, move: (lists: Record<string, Attachment[]>) => Record<string, Attachment[]>) => {
    listsRef.current = move(listsRef.current)
    budget.rekey(from, to)
  }, [budget])

  /** The ACTIVE composer's list as of now — for a callback captured earlier (the
   *  restore modal calls back with the one it took at the Restore click). */
  const liveList = useCallback((): Attachment[] => listsRef.current[activeKeyRef.current()] || [], [])

  /** `unsendableAttachmentNotice`, told as a toast; `true` = stop the send. */
  const refuseUnsendable = useCallback((list: Attachment[]): boolean => {
    const notice = unsendableAttachmentNotice(list)
    if (!notice) return false
    if (notice.level === 'error') toast.error(notice.message)
    else toast.info(notice.message)
    return true
  }, [toast])

  return { reserveInline, uploadSignal, pickFor, settled, cancel, discard, closeKey, beginSend, wasRemoved, keptForSend, carry, liveList, refuseUnsendable }
}
